/**
 * The login page (container): runs the backend's sign-in — Cognito SRP, or a
 * node's development token — and, once signed in, goes where the guard
 * bounced the user from (`?redirect=`, only if it is an in-app path).
 */
import { useNavigate, useSearch } from '@tanstack/react-router';
import { useRef, useState } from 'react';
import type { LoginOutcome } from '../auth/cognito.js';
import { LoginForm } from '../components/LoginForm.js';
import { useServices } from '../app/hooks.js';
import { safeRedirect } from '../lib/redirect.js';

function messageOf(err: unknown): string {
  return err instanceof Error ? err.message : String(err);
}

export function LoginView() {
  const { auth, backend } = useServices();
  const navigate = useNavigate();
  // The login route's own validated search (router.tsx validateSearch), not a re-parse.
  const redirectTo = safeRedirect(useSearch({ from: '/login' }).redirect ?? null);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [step, setStep] = useState<'credentials' | 'new-password'>('credentials');
  const challenge = useRef<((newPassword: string) => Promise<LoginOutcome>) | null>(null);

  const finish = async (outcome: LoginOutcome | null): Promise<void> => {
    if (outcome !== null && outcome.type === 'new-password-required') {
      challenge.current = outcome.complete;
      setStep('new-password');
      return;
    }
    // `href`, not `to`: the target may carry a query string (the guard stores `location.href`).
    await navigate({ href: redirectTo });
  };

  const run = async (attempt: () => Promise<LoginOutcome | null>): Promise<void> => {
    setPending(true);
    setError(null);
    try {
      await finish(await attempt());
    } catch (err) {
      setError(messageOf(err));
    } finally {
      setPending(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-background p-6">
      <LoginForm
        mode={auth.kind}
        step={step}
        target={`${backend.kind === 'cloud' ? 'Cloud' : 'Node'} · ${backend.label}`}
        pending={pending}
        error={error}
        onSubmit={({ identifier, password }) => {
          void run(async () => {
            if (auth.kind === 'dev') {
              await auth.login(identifier);
              return null;
            }
            return auth.login(identifier, password);
          });
        }}
        onSubmitNewPassword={(newPassword) => {
          const complete = challenge.current;
          if (complete === null) return;
          void run(() => complete(newPassword));
        }}
      />
    </div>
  );
}
